import { default as React, FC, Fragment, useEffect, useState } from 'react';

import { LeftOutlined } from '@ant-design/icons';
import { Button, Col, Layout, Modal, Row } from 'antd';
import routePaths from 'config/routes';
import { config } from 'config/config';
import { useIntl } from 'react-intl';
import * as api from 'services/api';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { setFooterHeight } from 'src/actions/appActions';
import { UpdateUserPoints } from 'src/actions/authActions';
import { ICheckoutData } from 'src/components/challenge/ChallengeCheckout';
import CustomModal from 'src/components/CustomModal';
import CheckoutAddressSelector from 'src/components/experienceCheckout/CheckoutAddressSelector';
import Link from 'src/components/Link';
import { IRootReducers } from 'src/reducers';
import { CheckoutTypeEnum } from 'src/shared/enums';
import {
  IAddressData,
  IChallenge,
  IChallengeCheckoutPayment,
  ICheckoutRequest,
  ISessionCheckoutPaymentRequest,
  IUser,
} from 'src/shared/models';
import {
  checkUserResourcePoints,
  customFormatMessage,
  dispatchCustomEvent,
  getTaxCountry,
  getUserAddressList,
  isCorrectCheckoutAddress,
  slugify,
} from 'utils';
import CheckoutPaymentDetails from './CheckoutPaymentDetails';
import CheckoutPaymentEnd from './CheckoutPaymentEnd';
import { PaymentCheckout } from './PaymentCheckout';
import BasicHeader from '../layout/BasicHeader';

const { USER_ADDRESS_SHIPPING } = CheckoutTypeEnum;
const { ASSET_PREFIX } = config.APP;

interface OwnProps {
  checkoutData: ICheckoutData;
  config: IChallengeCheckoutPayment;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  updateChallenge: React.Dispatch<React.SetStateAction<IChallenge>>;
  updateUserPoints: (
    userPoints: number,
  ) => (dispatch: Dispatch<UpdateUserPoints>) => void;
  user: IUser;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    userPoints: state.auth.userPoints,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ setFooterHeight }, dispatch);

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const CheckoutPayment: FC<Props> = ({
  challenge,
  checkoutData,
  config,
  loading,
  setLoading,
  setShowModal,
  showModal,
  updateChallenge,
  updateUserPoints,
  user,
  userPoints,
  setFooterHeight,
}) => {
  const { formatMessage } = useIntl();
  const { buttonName, checkoutName, product, points } = config;
  const [selectedAddress, setSelectedAddress] = useState<IAddressData>(null);
  const [addressList, setAddressList] = useState<IAddressData[]>(null);

  const [taxLabel, setTaxLabel] = useState(null);
  const [remainingChallengePoints, setRemainingChallengePoints] = useState<
    number | null
  >(null);
  const [paymentStep, setPaymentStep] = useState('address'); // address, pay, pay-success, pay-error
  const [successStatus, setSuccessStatus] = useState(false);
  const [checkoutProductsData, setCheckoutProductsData] =
    useState<ICheckoutRequest>();
  const [checkoutInfo, setCheckoutInfo] = useState();
  const [paymentMethod, setPaymentMethod] = useState();
  const [cardNumber, setCardNumber] = useState();
  const [sellResponse, setSellResponse] = useState(null);
  const [loader, setLoader] = useState(false);

  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    points,
    challenge?.challengeType,
  );
  const pointsLabel = Math.abs(remainingChallengePoints);

  const isSendButtonDisable = !isCorrectCheckoutAddress(selectedAddress);
  const singleProduct = true;
  const showCheckoutButton = !showModal && config.isChallengeInDate;

  useEffect(() => {
    if (showCheckoutButton) {
      setFooterHeight('checkout');
    }
    return () => {
      setFooterHeight(null);
    };
  }, [showModal, config.isChallengeInDate]);

  useEffect(() => {
    async function fetchInitialData() {
      const userAddressList = await getUserAddressList();
      setAddressList(userAddressList);
      const tax = await getTaxCountry();
      setTaxLabel(((product?.price * tax.tax) / (tax.tax + 100)).toFixed(1));
    }
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (challenge?.challengeResources) {
      const { challengeResources } = challenge;
      const remainingChallengePoints = challengeResources
        .filter(
          (resource) =>
            resource.config?.points && resource.config.isCompleted === false,
        )
        .reduce((prev, curr) => prev + curr.config.points, 0);

      setRemainingChallengePoints(remainingChallengePoints);
    }
  }, [challenge, userPoints]);

  const goToPayment = async () => {
    let requestStatus;
    setLoading(true);

    const { ...baseCheckoutData } = checkoutData;
    const data: ICheckoutRequest = {
      ...baseCheckoutData,
      type: USER_ADDRESS_SHIPPING,
    };
    data.address = selectedAddress;
    setCheckoutProductsData(data);

    const dataToSession: ISessionCheckoutPaymentRequest = {
      checkoutData: data,
    };

    if (dataToSession && hasUserEnoughPoints) {
      try {
        const response = await api.createAdyenCheckout(dataToSession);
        setSellResponse(response.data);
        setPaymentStep('pay');
        setLoading(false);
      } catch (error) {
        setPaymentStep('pay-error');
        setLoading(false);
      }
    }
  };

  const onPaymentCompleted = async (response) => {
    setLoader(true);
    let requestStatus;
    if (response?.paymentResponse?.resultCode === 'Success') {
      const score = response.userResourceCallback.score;
      setCheckoutInfo(sellResponse.checkout[0]);
      updateChallenge(response.userResourceCallback.challenge);
      if ((score || score === 0) && typeof score === 'number') {
        updateUserPoints(score);
      }
      setPaymentStep('pay-success');
      setSuccessStatus(true);
      requestStatus = 'ok';
      setLoader(false);
    } else if (
      [
        'Authorised',
        'Pending',
        'AuthenticationFinished',
        'AuthenticationNotRequired',
        'Received',
      ].includes(response?.paymentResponse?.resultCode)
    ) {
      try {
        const checkoutId = sellResponse.checkout[0];
        await api.waitingAdyenCheckout({
          checkoutId: checkoutId.BSTKD,
        });

        const score = response.userResourceCallback.score;
        setCheckoutInfo(sellResponse.checkout[0]);
        updateChallenge(response.userResourceCallback.challenge);
        if ((score || score === 0) && typeof score === 'number') {
          updateUserPoints(score);
        }

        requestStatus = 'send';
        setPaymentStep('pay-success');
        setSuccessStatus(true);
        setLoader(false);
      } catch (err) {
        setLoader(false);
        setPaymentStep('pay-error');
      }
    } else {
      requestStatus = 'ko';
      setLoader(false);
      setPaymentStep('pay-error');
    }
  };

  const onError = async () => {
    setPaymentStep('pay-error');
    setLoader(false);
  };

  const errorModalContent = (
    <div className="error-modal-payment">
      <Row className="error-modal-payment__row">
        <Col>
          <p className="checkout-payment-gateway__error-modal__title">
            {formatMessage({
              id: 'experience.error-checkout-payment.title',
            })}
          </p>
        </Col>
      </Row>
      <Row className="error-modal-payment__row">
        <Col>
          <p className="checkout-payment-gateway__error-modal__subtitle">
            {formatMessage({
              id: 'experience.error-checkout-payment.subtitle',
            })}
          </p>
        </Col>
      </Row>
      <Row className="error-modal-payment__row_right">
        <Button
          className="btn-primary btn-primary__small checkout-payment-gateway__error-modal__button"
          onClick={() => {
            setPaymentStep('address');
          }}>
          {formatMessage({ id: 'experience.error-checkout-payment.button' })}
        </Button>
      </Row>
    </div>
  );

  const checkoutModal = () => {
    const checkoutError = (
      <div className="checkout-modal">
        <CustomModal
          content={errorModalContent}
          footer={null}
          onCancel={() => {
            setPaymentStep('address');
          }}
          showIcon={false}
          visible={Boolean(showModal && paymentStep === 'pay-error')}
          customClass="error-modal-classnames"
        />
      </div>
    );

    const addressSelector = (
      <div className="checkout-modal__left-column--container checkout-modal__left-column--container-selector">
        <CheckoutAddressSelector
          addressList={addressList}
          selectedAddress={selectedAddress}
          setAddressList={setAddressList}
          setSelectedAddress={setSelectedAddress}
          type={USER_ADDRESS_SHIPPING}
        />
      </div>
    );

    const headerClassName =
      'container--no-padding basicHeader__container basicHeader__container-fixed';

    return (
      <Modal
        visible={showModal}
        className="checkout-modal"
        footer={null}
        mask={false}
        maskTransitionName={''}
        transitionName={''}
        onCancel={() => setShowModal(false)}>
        {paymentStep === 'pay-success' ? (
          <BasicHeader />
        ) : (
          <div className={`header-shadow header-fixed ${headerClassName}`}>
            <div className="container">
              <Layout.Header className="basicHeader">
                <Row
                  justify={'center'}
                  align="middle"
                  className={`basicHeader__wrapper--desktop basicHeader__wrapper--desktop--no-wrap`}>
                  <img
                    className="logo-modal-payment"
                    src={`${ASSET_PREFIX}/isdin_logo.png`}
                  />
                </Row>
                <Row justify="center" className="basicHeader__wrapper--mobile">
                  <Col span={12}>
                    <Row justify="center" align="middle">
                      <img
                        alt="logo"
                        loading="lazy"
                        className="logo-modal-payment"
                        src={`${ASSET_PREFIX}/isdin_logo_mobile.png`}
                      />
                    </Row>
                  </Col>
                </Row>
              </Layout.Header>
            </div>
          </div>
        )}

        {paymentStep === 'address' && (
          <>
            <div className="container payment__modal__col checkout-payment__content_back">
              <div className="checkout-payment__content">
                <div className="payment__where-lead">
                  <div className="payment__modal__left-column--container">
                    <button
                      onClick={() => setShowModal(false)}
                      className="checkout-payment__back__letter">
                      <LeftOutlined />
                      {formatMessage({ id: 'button.back' })}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="container payment__modal__col">
              {/** LEFT COLUMN */}
              <div className="payment__modal__left-column checkout-payment__content">
                <div className="payment__where-lead">
                  <div className="payment__modal__left-column--container">
                    {config.shippingQuestion ||
                      formatMessage({
                        id: 'page.experience.checkout.type-title',
                      })}
                  </div>
                </div>
                <div className="checkout-modal__left-column--container">
                  {addressSelector}
                </div>
              </div>

              {/** RIGHT COLUMN */}
              <div className="payment__modal__right-column">
                <div className="payment__modal__right-column--container">
                  <CheckoutPaymentDetails
                    buttonDisabled={isSendButtonDisable}
                    challengeType={challenge?.challengeType}
                    checkoutName={checkoutName}
                    loading={loading}
                    performCheckout={goToPayment}
                    product={product}
                    singleProduct={singleProduct}
                    termsLabel={config.termsLabel}
                    termsUrl={config.termsUrl}
                    userPoints={userPoints}
                    taxLabel={taxLabel}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {paymentStep === 'pay' && (
          <PaymentCheckout
            session={sellResponse?.session}
            config={sellResponse?.config}
            newCheckout={sellResponse.checkout[0]}
            checkoutData={checkoutProductsData}
            onPaymentCompleted={onPaymentCompleted}
            onError={onError}
            setPaymentStep={setPaymentStep}
            setBrandCard={setPaymentMethod}
            setCardNumber={setCardNumber}
            loader={loader}
            setLoader={setLoader}
          />
        )}

        {paymentStep === 'pay-success' && (
          <CheckoutPaymentEnd
            checkoutName={checkoutName}
            product={product}
            singleProduct={singleProduct}
            user={user}
            selectedAddress={selectedAddress}
            taxLabel={taxLabel}
            checkoutData={checkoutInfo}
            cardNumber={cardNumber}
            paymentMethod={paymentMethod}
          />
        )}

        {paymentStep === 'pay-error' && !successStatus && checkoutError}
      </Modal>
    );
  };

  const checkoutButtonBalance = () => {
    const renderBalance =
      challenge?.challengePoints || challenge?.challengePoints === 0;
    if (!renderBalance) return <Fragment />;

    if (hasUserEnoughPoints)
      return (
        <div className="experience-checkout-button__points">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              {customFormatMessage(
                'page.experience.checkout.user-balance-has-points',
              )}
            </p>
          </div>
        </div>
      );

    return (
      <div className="experience-checkout-button__points">
        <div className="experience-checkout-button__points-balance">
          <p className="regular-body experience-checkout-button__points-balance__text">
            {customFormatMessage(
              'page.experience.checkout.user-balance-lacks-{points}',
              {
                points: Number(pointsLabel - userPoints),
              },
            )}
          </p>
        </div>

        <div className="experience-checkout-button__points-link">
          <Link href={routePaths.PAGES.SCAN.SCAN_INDEX}>
            <Button className="experience-checkout-button__points-link--button">
              {formatMessage({
                id: 'page.experience.checkout.user-balance-get-more-points',
              })}
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  const handleShowModal = (): void => {
    const requestStatus = hasUserEnoughPoints ? 'send' : 'ko';

    dispatchCustomEvent({
      eventCategory: 'experience',
      eventAction: 'request',
      eventLabel: requestStatus,
      eventName: `checkout-payment-modal-${requestStatus}`,
      eventDetail: slugify(
        `${challenge.challengeName}_${challenge.challengeId}`,
      ),
    });

    hasUserEnoughPoints && setShowModal(true);
  };

  const buttonContent = (
    <>
      {checkoutButtonBalance()}
      <Button
        disabled={config.isBlocked || !hasUserEnoughPoints || user.blacklisted}
        loading={loading}
        className={`btn-primary btn-primary--black experience-checkout-button ${
          singleProduct ? 'experience-checkout-button__single-product' : ''
        }`}
        onClick={() => handleShowModal()}>
        <div className="experience-checkout-button__container">
          {buttonName}
        </div>
      </Button>
    </>
  );

  const checkoutButton = (
    <>
      <div className="show_on_breakpoint-sm experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        {buttonContent}
      </div>
      <div className="not_show_on_breakpoint-sm experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        <div className="container experience-checkout-button__container">
          <div>
            <img
              className="experience-checkout-button__container--image"
              src={product.img}
              alt="checkout-img"></img>
            <div className="experience-checkout-button__container--title">
              <span>{challenge?.name}</span>
            </div>
          </div>
          <div>{buttonContent}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {showCheckoutButton && checkoutButton}
      {checkoutModal()}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutPayment);
